import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { loadStripe } from '@stripe/stripe-js';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { appPlans } from '../config/envConfig';
import { getButtonSuccessStyleClass } from '../config/FormSettings';
import { ApiRoutesEnum } from '../routes/ApiRoutesEnum';
import { AuthLinkLabelsDisplayEnum } from '../shared//helpers/AuthLinkLabelsDisplayEnum';
import { useIsLoading } from '../shared/hooks/useIsLoading';
import loggerService from '../shared/services/LoggerService';
import notificationService from '../shared/services/NotificationService';
import LoginAuth from '../auth/components/LoginAuth';
import AuthModal from '../auth/components/modals/AuthModal';
import SignupAuth from '../auth/components/SignupAuth';
import axiosInstance, { CustomAxiosRequestConfig } from '../auth/config/axiosInstance';
import { useAuthContext } from '../auth/contexts/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string);

const Checkout: React.FC = () => {
  const { isLoggedIn } = useAuthContext();
  const { isLoading, setIsLoading } = useIsLoading();
  const location = useLocation();

  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
  const [authLinkLabelsDisplayEnum, setAuthLinkLabelsDisplayEnum] = useState<AuthLinkLabelsDisplayEnum>(AuthLinkLabelsDisplayEnum.LOGIN);

  const handleCheckout = async () => {
    try {
      setIsLoading(true);

      if (!await isValidAuth())
        return;

      const returnPath = `${location.pathname}${location.search}`;

      const stripe = await stripePromise;

      if (!stripe)
        throw new Error("Failed to load stripePromise");

      var formData = {
        returnPath: returnPath,
      }

      const response = await axiosInstance.post(
        ApiRoutesEnum.CREATE_CHECKOUT_SESSION,
        formData,
        { authNeeded: true } as CustomAxiosRequestConfig);

      const { sessionId } = response.data;

      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        throw error;
      }
    } catch (error) {
      loggerService.error(error);

      notificationService.setMessage({
        message: "Failed to create payment",
        status: false,
      });
    }
    finally {
      setIsLoading(false);
    }
  };

  const isValidAuth = async (): Promise<boolean> => {
    if (!(await isLoggedIn())) {
      setIsAuthModalOpen(true);

      return false;
    }

    return true;
  };

  return (
    <>
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-2xl font-bold mb-6 text-center">Choose Your Package</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {appPlans.map((pkg) => (
            <div
              key={pkg.name}
              className={`border rounded-2xl shadow-lg p-6 transition-all duration-300 ${pkg.highlight ? "border-success ring-2 ring-success bg-successBackground/10" : "border-gray-300"
                }`}
            >
              <h3 className="text-xl font-semibold mb-2">{pkg.name}</h3>
              <div className="text-3xl font-bold text-success mb-4">
                {pkg.price === 0 ? "Free" : (
                  <>
                    <span className="text-sm align-top">$</span>{pkg.price.toFixed(2)}
                  </>
                )}
              </div>
              <ul className="text-sm text-gray-700 space-y-2 mb-4">
                {pkg.features.map((feature: any, i) => (
                  <li key={i} className={`flex items-center gap-x-2 ${!feature.enabled && "line-through"}`}>
                    <FontAwesomeIcon icon={faCheck} className="text-success" />
                    {feature.name}
                  </li>
                ))}
              </ul>
              {pkg.button && (
                <button className={`${getButtonSuccessStyleClass(true)} w-full`} onClick={handleCheckout}
                  disabled={isLoading}
                >
                  {isLoading ? "Rolling payment..." : "Upgrade Now"}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)}>
        {authLinkLabelsDisplayEnum === AuthLinkLabelsDisplayEnum.LOGIN ? (
          <LoginAuth
            isAuthModal={true}
            setIsModalOpen={setIsAuthModalOpen}
            setAuthLinkLabelsDisplayEnum={setAuthLinkLabelsDisplayEnum}
            authLinkLabelsDisplayEnum={authLinkLabelsDisplayEnum}
          />
        ) :
          <SignupAuth
            isAuthModal={true}
            setIsModalOpen={setIsAuthModalOpen}
            setAuthLinkLabelsDisplayEnum={setAuthLinkLabelsDisplayEnum}
            authLinkLabelsDisplayEnum={authLinkLabelsDisplayEnum}
          />
        }
      </AuthModal>
    </>
  );
};

export default Checkout;